import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { isDeletedNickname } from '@tg/shared';
import { botName } from './botNames';

/**
 * Nickname for display: an erased account keeps the stored constant `DELETED_NICKNAME` (shared rounds, tournament
 * places stay); it is shown as `common.deletedPlayer` in the viewer's language.
 */
export function useNickname(): (nickname: string) => string {
  const intl = useIntl();
  return useCallback((n: string) => (isDeletedNickname(n) ? intl.formatMessage({ id: 'common.deletedPlayer' }) : n), [intl]);
}

/** Number of a table bot: the trailing number of its server name (`机器人-2`), else the seat (0-based) + 1. */
export function botNumber(nickname: string, seat: number): number {
  const m = /(\d+)\s*$/.exec(nickname);
  return m ? Number(m[1]) : seat + 1;
}

interface SeatLike {
  seat: number;
  player: { nickname: string; bot?: boolean } | null;
}

/**
 * Display name of a table seat's player: bots get a playful name in the viewer's language (botNames.ts, seeded by the
 * table so everyone sees the same) instead of the server's fixed `机器人-2` — `common.botName` («Bot 2») when there is
 * no table; humans — `useNickname`. null for an empty seat.
 * `tag` (default): a bot's name carries «· bot» (`common.botTagged`) — pass false only where a BOT badge sits next to it.
 */
export function usePlayerName(
  table?: string | null,
  { tag = true }: { tag?: boolean } = {},
): (seat: SeatLike | null | undefined) => string | null {
  const intl = useIntl();
  return useCallback(
    (s: SeatLike | null | undefined) => {
      const p = s?.player;
      if (!s || !p) return null;
      if (p.bot) {
        const n = botNumber(p.nickname, s.seat);
        if (!table) return intl.formatMessage({ id: 'common.botName' }, { n });
        const name = botName(table, n, intl.locale);
        return tag ? intl.formatMessage({ id: 'common.botTagged' }, { name }) : name;
      }
      return isDeletedNickname(p.nickname) ? intl.formatMessage({ id: 'common.deletedPlayer' }) : p.nickname;
    },
    [intl, table, tag],
  );
}

/**
 * Name of the house bot of a shared round (Pot, Red Envelopes): a playful name from `botNames.ts`, seeded by the round
 * (`potBotSeed` / `hongbaoBotSeed`) so every viewer sees the same one. `tag` (default): «Sleepy Panda · bot»
 * (`common.botTagged`) — pass false only where a «bot» badge sits next to the name.
 */
export function useHouseBotName({ tag = true }: { tag?: boolean } = {}): (seed: string) => string {
  const intl = useIntl();
  return useCallback(
    (seed: string) => {
      const name = botName(seed, 1, intl.locale);
      return tag ? intl.formatMessage({ id: 'common.botTagged' }, { name }) : name;
    },
    [intl, tag],
  );
}
